/** Shared visual tokens for Settings: warm card, inputs blend with card, compact, subtle focus. */

export const settingsCardBg = 'var(--surface-sunken)';

/** Inputs sit on card fill: same bg as shell, hairline border only on focus */
export const settingsInputClass =
  'w-full rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 py-1.5 text-[12px] leading-snug text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] transition-[border-color,box-shadow] duration-150 focus:outline-none focus:ring-0 focus:border-[color:var(--border-strong)] focus:shadow-none';

export const settingsTextareaClass =
  'w-full min-h-[72px] rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 py-1.5 text-[12px] leading-snug text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] transition-[border-color] duration-150 focus:outline-none focus:ring-0 focus:border-[color:var(--border-strong)] resize-y';

export const settingsSelectClass =
  'w-full h-9 rounded-md border border-[color:var(--border-default)] bg-surface pl-2.5 pr-8 text-[12px] text-[color:var(--text-primary)] cursor-pointer transition-[border-color] duration-150 focus:outline-none focus:ring-0 focus:border-[color:var(--border-strong)] appearance-none';

export const settingsCardShell =
  'rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] overflow-hidden';

/**
 * Keyboard focus ring.
 *
 * These buttons drew no ring of their own, so they fell back to the UA default
 * — which varies by browser and, in Chrome, is a dark outline that all but
 * disappears against the near-black `--surface-sunken` card in dark mode. The
 * app already has `--state-focus-ring` for this (16 other files use it); it
 * carries a per-theme alpha, 0.35 light and 0.45 dark, so the brand ring stays
 * legible on both the light card and the dark one.
 */
export const settingsFocusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';

/**
 * Primary action — brand fill.
 *
 * Hover moves to `--brand-primary-hover` rather than dropping opacity. A
 * translucent orange composites toward whatever is behind it, so `hover:opacity-90`
 * made the button LIGHTER on a white card and DARKER on the dark one — opposite
 * directions for the same interaction. The token is #e55f1f in both modes.
 */
export const settingsPrimaryButtonClass =
  `inline-flex items-center gap-1.5 rounded-md bg-brand px-2.5 py-1.5 text-[11px] font-medium text-[color:var(--text-on-brand)] transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-60 ${settingsFocusRing}`;

/** Secondary action — matches the bordered/sunken pattern used across Settings. */
export const settingsSecondaryButtonClass =
  `inline-flex items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-2.5 py-1.5 text-[11px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-60 ${settingsFocusRing}`;

/** Tertiary action — brand-coloured text, no chrome. */
export const settingsLinkButtonClass =
  `rounded text-[11px] font-medium text-[color:var(--brand-primary)] transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-60 ${settingsFocusRing}`;

/** Brand page chrome shared by the Project / Integrations / Models pages. */

/** Semibold section title over a hairline. */
export const settingsSectionTitleClass =
  'border-b border-[color:var(--border-default)] pb-1.5 text-[13px] font-semibold text-[color:var(--text-primary)]';

/** Primary: solid ember, on-brand text, same h-8 as the table toolbar. */
export const settingsSolidButtonClass =
  `inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-brand px-3 text-[12px] font-medium text-[color:var(--text-on-brand)] transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50 ${settingsFocusRing}`;

/** Small icon-only action (copy, edit, delete) with no chrome until hover. */
export const settingsIconButtonClass =
  `inline-flex h-7 w-7 flex-none items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-40 ${settingsFocusRing}`;

/** Bordered rounded container for a list or table (Traces style). */
export const settingsTableShellClass =
  'overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface';

/** Plain feedback line: tinted text, no box. */
export const settingsErrorTextClass = 'text-[12px] text-[color:var(--feedback-error)]';
export const settingsOkTextClass = 'text-[12px] text-[color:var(--feedback-success)]';

/** Modal shell used by every Settings dialog. */
export const settingsModalClass =
  'w-full rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised,var(--surface-base))] shadow-token-lg';
